import { useState, useMemo } from 'react';
import { cloneDeep } from 'lodash-es';
import { isEmpty, messageTips, goToProjectDetailsPage } from '@gp/utils/common';
import { FIELD_COMMON_ERR_MSG } from '@gp/constants/const';
import {
  FIELDS,
  CONTROLS,
  VALIDATORS,
  VALIDATIONS,
  FIELDS_BASIC,
  FIELDS_TEAM,
  FIELDS_GAME,
} from '../dynamicFormCfg';
import { useMemoizedFn, useDeepCompareEffect, useRequest } from 'ahooks';
import type { SelectOption } from 'tea-component';
import type {
  IDynamicFormField,
  IDynamicFormFieldValidation,
} from '@gp/components/DynamicForm/types';
import type {
  IDynamicBaseFormControl,
  IDynamicFormControl,
} from '@gp/components/DynamicForm/components/FormControl/types';
import type { IStudioDTO } from '@gp/pages/Workspace/Product/types';
import type { IProjectInformationVO } from '../types';
import { fetchCommonConditions } from '@gp/services/filter';
import useTeamGameInfo from '@gp/pages/Pipeline/ProjectDetails/GameDetail/components/TeamGameInformation/hooks/useTeamGameInfo';
import { getEditProductStudioList } from '@gp/services/project';
import { updateProjectInformation } from '@gp/services/projectDetail';
import {
  fetchProjectTechnicalsConditions,
  editProjectTechnicals,
} from '@gp/services/projectTechnical';
import {
  formatStudios,
  formatEngineOptionalRes,
  formatCondOptionByKey,
  formatEngineOptionsRes,
} from '@gp/pages/Workspace/Product/utils';
import { formatTechnicalsConditions } from '@gp/pages/Pipeline/ProjectDetails/Technical/components/EditTechnical/utils';

const DEFAULT_PROJECT_FORM_VALUE: any = {
  project: '',
  stage: '',
  pro_coa: '',
  pro_coa_id: '',
  studio_entity_id: '',
  studio_location: '',
  publisher: '',
  ip: [],
  genre: [],
  platform: [],
  business_model: '',
  engine: [],
  engine_optional: [],
  // group: {
  //   parent: '',
  //   child: '',
  // },
  // priority: '',
  // expanded_genre: '',
  // tag_line: '',
  // ambition: '',
};

const ALL_ERRORS: Record<string, string> = {
  project: FIELD_COMMON_ERR_MSG,
  stage: FIELD_COMMON_ERR_MSG,
  pro_coa: FIELD_COMMON_ERR_MSG,
  pro_coa_id: FIELD_COMMON_ERR_MSG,
  studio_entity_id: FIELD_COMMON_ERR_MSG,
  studio_location: FIELD_COMMON_ERR_MSG,
  publisher: FIELD_COMMON_ERR_MSG,
  // ip: FIELD_COMMON_ERR_MSG,
  genre: FIELD_COMMON_ERR_MSG,
  platform: FIELD_COMMON_ERR_MSG,
  business_model: FIELD_COMMON_ERR_MSG,
  engine: FIELD_COMMON_ERR_MSG,
};

const getStudioOptions = (studios?: IStudioDTO[]): SelectOption[] => {
  if (!Array.isArray(studios) || !studios.length) return [];
  return studios.map((studio) => {
    const { id, name } = studio || {};
    return { value: id || '', text: name || '' };
  });
};

const getListErrors = (key: string, value?: any[]): Record<string, string> => {
  if (!Array.isArray(value) || !value.length) return { [key]: FIELD_COMMON_ERR_MSG };
  const filteredValue = value.filter((v) => !isEmpty(v));
  return filteredValue.length ? {} : { [key]: FIELD_COMMON_ERR_MSG };
};

const getErrors = (values?: IProjectInformationVO): Record<string, string> => {
  if (!values) return ALL_ERRORS;
  const errors: Record<string, string> = {};
  Object.entries(values).forEach(([key, value]) => {
    const { required, validator } = VALIDATORS[key] || {};
    if (typeof validator === 'function') {
      const { status, message } = validator(value, values);
      if (status === 'error') {
        Object.assign(errors, { [key]: message });
      }
    } else if (required) {
      if (Array.isArray(value)) {
        Object.assign(errors, getListErrors(key, value));
      } else if (isEmpty(value)) {
        Object.assign(errors, { [key]: FIELD_COMMON_ERR_MSG });
      }
    }
  });
  return errors;
};

const getValidations = (
  values: IProjectInformationVO,
): Record<string, IDynamicFormFieldValidation> => {
  const errors = getErrors(values);
  const validationsTmp = cloneDeep(VALIDATIONS);
  Object.keys(validationsTmp).forEach((key) => {
    const error = errors?.[key];
    if (validationsTmp[key] && error) {
      validationsTmp[key].status = 'error';
      validationsTmp[key].message = error;
    }
  });
  return validationsTmp;
};

/**
 * Project 表单的逻辑
 */
export default function useProjectForm() {
  // 获取可选studio列表数据
  const { data: studiosData = [], loading: studiosDataLoading } =
    useRequest(getEditProductStudioList);

  const condParams = {
    bussiness_model: [],
    genre: [],
    ip: [],
    platform: [],
  };

  const { loading: filterLoading, data: filterList } = useRequest(fetchCommonConditions, {
    defaultParams: [condParams],
  });

  const { loading: technicalLoading, data: technicalDatas } = useRequest(
    fetchProjectTechnicalsConditions,
  );

  // 获取可选studio-PTeam列表数据
  // const { studioOptionList } = useTeamGameInfo({
  //   projectId: '',
  //   isNeedCommon: false,
  // });
  // 非空处理，并且排序后的studio列表数据
  const studios = useMemo<IStudioDTO[]>(() => {
    if (studiosDataLoading) return [];
    return formatStudios(studiosData);
  }, [studiosDataLoading, studiosData]);

  const loadingCur = useMemo(() => {
    return studiosDataLoading || filterLoading;
  }, [studiosDataLoading, filterLoading]);

  // 是否点击过confirm按钮进行提交表单
  const [submitted, setSubmitted] = useState(false);
  // const [engineOptionList, setEngineOptionList] = useState<any>([]);
  const [engineOptions, setEngineOptions] = useState<any>([]);
  const [engineOptional, setEngineOptional] = useState<any>([]);

  // 表单字段集合
  const [fields] = useState<IDynamicFormField[]>(FIELDS);
  const [fieldsBasic] = useState<IDynamicFormField[]>(FIELDS_BASIC);
  const [fieldsTeam] = useState<IDynamicFormField[]>(FIELDS_TEAM);
  const [fieldsGame] = useState<IDynamicFormField[]>(FIELDS_GAME);

  // 表单控件映射，键为表单字段key
  const [controls, setControls] = useState<Record<string, IDynamicBaseFormControl>>(CONTROLS);

  // 表单校验结果信息映射，键为表单字段key
  const [validations, setValidations] =
    useState<Record<string, IDynamicFormFieldValidation>>(VALIDATIONS);

  // 表单数据
  const [value, setValue] = useState<IProjectInformationVO>(DEFAULT_PROJECT_FORM_VALUE);

  const resetValue = () => {
    setSubmitted(false);
    setValidations(VALIDATIONS);
    setValue(DEFAULT_PROJECT_FORM_VALUE);
  };

  const handleValidate = (
    payload: IProjectInformationVO,
    submitted: boolean,
  ): Record<string, IDynamicFormFieldValidation> => {
    if (!submitted) return validations;
    const validationsTmp = getValidations(payload);
    setValidations({ ...validationsTmp });
    return validationsTmp;
  };

  const handleChange = (payload: IProjectInformationVO) => {
    const valueTmp = { ...value, ...payload };
    setValue(valueTmp);
    handleValidate(valueTmp, submitted);
  };

  const handleSubmit = (): IProjectInformationVO | null => {
    const validations = handleValidate(value, true);
    const isValid = Object.values(validations).every(
      (validation) => validation?.status === 'success',
    );
    setSubmitted(true);
    return isValid ? value : null;
  };

  const updateControl = (field: string, payload: Partial<IDynamicFormControl>) => {
    if (!field) return;
    const controlsTmp = {
      ...controls,
      [field]: {
        ...controls?.[field],
        ...payload,
      },
    };
    setControls(controlsTmp);
  };
  const updateControlList = (optionsList: any) => {
    if (!optionsList) return;
    let controlsTmp = {
      ...controls,
    };

    if (Array.isArray(optionsList)) {
      optionsList?.forEach((item: any) => {
        if (item?.key && !isEmpty(item?.options)) {
          controlsTmp = {
            ...controlsTmp,
            [item?.key]: {
              ...controls?.[item?.key],
              options: item?.options,
            },
          };
        }
      });
    }

    setControls(controlsTmp);
  };

  useDeepCompareEffect(() => {
    if (studiosDataLoading) return;
    updateControl('studio_entity_id', {
      options: getStudioOptions(studios),
      studioOptionList: studios,
    });
  }, [studiosDataLoading, studios]);

  useDeepCompareEffect(() => {
    if (filterLoading) return;

    const platformOptions = formatCondOptionByKey(filterList, 'platform');
    const bussinessModelOptions = formatCondOptionByKey(filterList, 'bussiness_model');
    const ipOptions = formatCondOptionByKey(filterList, 'ip');
    const genreOptions = formatCondOptionByKey(filterList, 'genre');

    const optionsList = [
      {
        key: 'platform',
        options: platformOptions,
      },
      {
        key: 'bussiness_model',
        options: bussinessModelOptions,
      },
      {
        key: 'ip',
        options: ipOptions,
      },
      {
        key: 'genre',
        options: genreOptions,
      },
    ];

    updateControlList(optionsList);
  }, [filterLoading, filterList]);

  useDeepCompareEffect(() => {
    if (studiosDataLoading) return;
    const selectOption = studios?.find((item: any) => item.id === value?.studio_entity_id) || {};
    if (!isEmpty(selectOption)) {
      if (selectOption?.studio_location_city && selectOption?.studio_location_country) {
        const valueTmp = {
          ...value,
          studio_location: `${selectOption?.studio_location_city}, ${selectOption?.studio_location_country}`,
        };
        setValue(valueTmp);
        const validationsTmp = {
          ...validations,
          studio_location: {
            message: '',
            status: 'success',
          },
        } as any;
        // setSubmitted(true);
        setValidations(validationsTmp);
      }
    }
  }, [value?.studio_entity_id, studiosDataLoading, studios]);

  useDeepCompareEffect(() => {
    if (technicalLoading) return;
    const technicalsConditions = formatTechnicalsConditions(technicalDatas);

    const engineList = technicalsConditions?.engine || [];
    const engineOptionsValue = formatEngineOptionsRes(engineList);
    const engineOptionalValue = formatEngineOptionalRes(engineList);

    updateControl('engine', {
      options: engineOptionsValue,
    });

    setEngineOptions(engineOptionsValue);
    setEngineOptional(engineOptionalValue);
  }, [technicalLoading, technicalDatas]);

  return {
    fields,
    fieldsBasic,
    fieldsTeam,
    fieldsGame,
    controls,
    validations,
    value,
    setValue,
    resetValue,
    onChange: handleChange,
    onSubmit: handleSubmit,
    loading: loadingCur,
    engineOptions,
    engineOptional,
    setEngineOptional,
  };
}
